import type { Variants } from 'framer-motion'

/**
 * Standard transition durations for the application.
 */
export const durations = {
  fast: 0.2,
  default: 0.3,
  slow: 0.5,
  stagger: 0.1,
} as const

/**
 * Common EASING functions for fluid animations.
 */
const EASE_X1 = 0.33
const EASE_Y1 = 1
const EASE_X2 = 0.68
const EASE_Y2 = 1

const EASE_IN_X1 = 0.32
const EASE_IN_Y1 = 0
const EASE_IN_X2 = 0.67
const EASE_IN_Y2 = 0

const EASE_IN_OUT_X1 = 0.65
const EASE_IN_OUT_Y1 = 0
const EASE_IN_OUT_X2 = 0.35
const EASE_IN_OUT_Y2 = 1

const EASE_OUT_CUBIC = [EASE_X1, EASE_Y1, EASE_X2, EASE_Y2] as [number, number, number, number]
const EASE_IN_CUBIC = [EASE_IN_X1, EASE_IN_Y1, EASE_IN_X2, EASE_IN_Y2] as [
  number,
  number,
  number,
  number,
]
const EASE_IN_OUT_CUBIC = [EASE_IN_OUT_X1, EASE_IN_OUT_Y1, EASE_IN_OUT_X2, EASE_IN_OUT_Y2] as [
  number,
  number,
  number,
  number,
]

export const easings = {
  easeOutCubic: EASE_OUT_CUBIC,
  easeInCubic: EASE_IN_CUBIC,
  easeInOutCubic: EASE_IN_OUT_CUBIC,
  spring: { type: 'spring', stiffness: 300, damping: 30 },
} as const

/**
 * Reusable Framer Motion variants.
 */
export const animationVariants: Record<string, Variants> = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: durations.default } },
    exit: { opacity: 0, transition: { duration: durations.fast } },
  },

  slideUp: {
    initial: { opacity: 0, y: 20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: durations.default, ease: easings.easeOutCubic },
    },
    exit: {
      opacity: 0,
      y: 20,
      transition: { duration: durations.fast, ease: easings.easeInCubic },
    },
  },

  slideDown: {
    initial: { opacity: 0, y: -20 },
    animate: {
      opacity: 1,
      y: 0,
      transition: { duration: durations.default, ease: easings.easeOutCubic },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: durations.fast, ease: easings.easeInCubic },
    },
  },

  scaleIn: {
    initial: { opacity: 0, scale: 0.95 },
    animate: {
      opacity: 1,
      scale: 1,
      transition: { duration: durations.default, ease: easings.easeOutCubic },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: durations.fast, ease: easings.easeInCubic },
    },
  },

  staggerContainer: {
    animate: {
      transition: {
        staggerChildren: durations.stagger,
      },
    },
  },

  itemFadeIn: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: durations.default } },
    exit: { opacity: 0, y: 10 },
  },
}
